import { unwrap } from "@sagot/api-client";
import { planAtLeast, WORKFLOW_STEP_MIN_PLAN } from "@sagot/constants";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceDetails, useWorkspaceId } from "../../../session/workspace-context";
import { attributeDefinitions, type AttributeDefinition, type AttributeInput, type AttributePatch, type SlaPolicy, type SlaPolicyInput } from "./p3-model";

const REFERENCE_STALE_MS = 60_000;

/** Query keys of the phase 3 settings objects (under `["ws", wsId]` like every workspace key). */
export const p3Keys = {
  attributes: (wsId: string) => ["ws", wsId, "settings", "attributes"] as const,
  slaPolicies: (wsId: string) => ["ws", wsId, "settings", "sla-policies"] as const,
};

/** Custom attribute definitions (Settings, the condition editor and the Set attribute step). */
export function useAttributes(enabled = true): UseQueryResult<AttributeDefinition[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: p3Keys.attributes(wsId),
    queryFn: async () => attributeDefinitions(await unwrap(api.GET("/v1/attributes"))),
    enabled,
    retry: false,
    staleTime: REFERENCE_STALE_MS,
  });
}

/** Create, edit (label and choices only) and delete; a delete is refused with a 409 while a live workflow uses the attribute. */
export function useAttributeMutations() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  const settle = () => client.invalidateQueries({ queryKey: p3Keys.attributes(wsId) });
  const create = useMutation({
    mutationFn: (body: AttributeInput) => unwrap(api.POST("/v1/attributes", { body })),
    onSuccess: settle,
  });
  const update = useMutation({
    mutationFn: (input: { id: string; body: AttributePatch }) =>
      unwrap(api.PATCH("/v1/attributes/{attribute_id}", { params: { path: { attribute_id: input.id } }, body: input.body })),
    onSuccess: settle,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/attributes/{attribute_id}", { params: { path: { attribute_id: id } } })),
    onSuccess: settle,
  });
  return { create, update, remove };
}

const SLA_PLAN = WORKFLOW_STEP_MIN_PLAN.apply_sla ?? "institution";

/**
 * Whether the workspace's plan has SLA policies. Nothing is asked of the API until the plan is known
 * (`ready`); a workspace whose plan cannot be read is left to the server (a 402 still gates it).
 */
export function useSlaPlan(): { ready: boolean; blocked: boolean } {
  const details = useWorkspaceDetails();
  if (details.isPending) return { ready: false, blocked: false };
  const plan = details.data?.plan;
  return { ready: true, blocked: plan !== undefined && !planAtLeast(plan, SLA_PLAN) };
}

/** SLA policies (Institution: other plans get a 402 the caller turns into an upgrade state). */
export function useSlaPolicies(enabled = true): UseQueryResult<SlaPolicy[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: p3Keys.slaPolicies(wsId),
    queryFn: () => unwrap(api.GET("/v1/sla-policies")),
    enabled,
    retry: false,
    staleTime: REFERENCE_STALE_MS,
  });
}

/** Create, edit and delete; a duplicate name and a delete while a live workflow applies the policy are 409s. */
export function useSlaMutations() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  const settle = () => client.invalidateQueries({ queryKey: p3Keys.slaPolicies(wsId) });
  const create = useMutation({
    mutationFn: (body: SlaPolicyInput) => unwrap(api.POST("/v1/sla-policies", { body })),
    onSuccess: settle,
  });
  const update = useMutation({
    mutationFn: (input: { id: string; body: SlaPolicyInput }) =>
      unwrap(api.PATCH("/v1/sla-policies/{policy_id}", { params: { path: { policy_id: input.id } }, body: input.body })),
    onSuccess: settle,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/sla-policies/{policy_id}", { params: { path: { policy_id: id } } })),
    onSuccess: settle,
  });
  return { create, update, remove };
}
